import React, { useEffect, useState } from 'react';
import { getItemDecisionHistory, recordOutcomeObservation } from '../../lib/decisionIntelligenceService';
import { SearchableItemPicker } from '../prioritization/SearchableItemPicker';
import type { RecordOutcomeInput, OutcomeType } from '../../types/decisionIntelligence';

interface Props {
  workspaceId: string;
  itemId?: string;
  itemTitle?: string;
  onRecorded?: (observationId: string, attributionsComputed: boolean) => void;
  onCancel?: () => void;
}

const OUTCOME_TYPES: { value: OutcomeType; label: string; emoji: string; description: string }[] = [
  { value: 'shipped',   label: 'Shipped',   emoji: '🚀', description: 'Delivered as planned'       },
  { value: 'succeeded', label: 'Succeeded', emoji: '✅', description: 'Goals fully achieved'        },
  { value: 'partial',   label: 'Partial',   emoji: '🔶', description: 'Partially achieved'          },
  { value: 'delayed',   label: 'Delayed',   emoji: '⏳', description: 'Completed late'              },
  { value: 'pivoted',   label: 'Pivoted',   emoji: '↩️', description: 'Changed direction mid-flight' },
  { value: 'cancelled', label: 'Cancelled', emoji: '❌', description: 'Did not proceed'             },
  { value: 'failed',    label: 'Failed',    emoji: '💥', description: 'Attempted but did not succeed' },
];

const SUCCESS_TYPES: OutcomeType[] = ['shipped', 'succeeded'];
const PARTIAL_TYPES: OutcomeType[] = ['partial', 'delayed', 'pivoted'];

function outcomeColorClass(type: OutcomeType): string {
  if (SUCCESS_TYPES.includes(type)) return 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:border-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]';
  if (PARTIAL_TYPES.includes(type)) return 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] dark:border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]';
  return 'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)] dark:border-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]';
}

/** What logging this outcome will score, read before the person saves. */
function signalsLine(count: number): string {
  if (count === 0) return 'No signal on this item yet, so this outcome will score nobody.';
  return `This outcome scores ${count} signal${count === 1 ? '' : 's'} on this item.`;
}

export function OutcomeObservationPanel({ workspaceId, itemId, itemTitle, onRecorded, onCancel }: Props) {
  // An outcome scores the signals on its item, so it needs one.
  const [pickedItemId, setPickedItemId] = useState<string | undefined>(undefined);
  const chosenItemId = itemId ?? pickedItemId;
  const [signalCount, setSignalCount] = useState<{ itemId: string; count: number } | null>(null);
  const [outcomeType, setOutcomeType] = useState<OutcomeType>('shipped');
  const [qualityScore, setQualityScore] = useState<number>(7);
  const [notes, setNotes] = useState('');
  const [executionSource, setExecutionSource] = useState('manual');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chosenItemId || !workspaceId) return;
    let cancelled = false;
    getItemDecisionHistory(chosenItemId, workspaceId)
      .then(({ signals }) => {
        if (!cancelled) setSignalCount({ itemId: chosenItemId, count: signals.length });
      })
      .catch(() => {
        // The line is a courtesy; the save does not depend on it.
        if (!cancelled) setSignalCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [chosenItemId, workspaceId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const input: RecordOutcomeInput = {
      outcomeType,
      outcomeQualityScore: qualityScore,
      notes:               notes.trim() || undefined,
      executionSource,
    };

    try {
      const { observationId, attributionsComputed } = await recordOutcomeObservation(
        workspaceId, input, chosenItemId
      );
      onRecorded?.(observationId, attributionsComputed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record outcome.');
    } finally {
      setSaving(false);
    }
  }

  const qualityColor =
    qualityScore >= 8 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
    qualityScore >= 5 ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'     :
                        'text-[var(--coral)] dark:text-[var(--coral)]';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {itemId ? (
        itemTitle && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">
            Outcome for: <span className="text-[var(--text-pri)] dark:text-[var(--text-pri)] font-medium">{itemTitle}</span>
          </p>
        )
      ) : (
        <SearchableItemPicker
          value={pickedItemId}
          onChange={setPickedItemId}
          label="Which item is this about?"
          emptyLabel="Choose an item"
          allowEmpty={false}
          required
        />
      )}
      {signalCount && signalCount.itemId === chosenItemId && (
        <p data-testid="outcome-signal-count" className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
          {signalsLine(signalCount.count)}
        </p>
      )}

      {/* Outcome type grid */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-2">What happened?</label>
        <div className="grid grid-cols-2 gap-2">
          {OUTCOME_TYPES.map(ot => (
            <button
              key={ot.value}
              type="button"
              onClick={() => setOutcomeType(ot.value)}
              className={`text-left px-3 py-2  border text-xs transition-colors ${
                outcomeType === ot.value
                  ? outcomeColorClass(ot.value) + ' border-opacity-80'
                  : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)] hover:border-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--surface-raised)]/50 dark:text-[var(--text-sec)] dark:hover:border-[var(--text-ter)]'
              }`}
            >
              <span className="mr-1.5">{ot.emoji}</span>
              <span className="font-semibold">{ot.label}</span>
              <div className="text-[10px] opacity-70 mt-0.5">{ot.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Quality score */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Outcome quality</label>
          <span className={`text-lg font-bold tabular-nums ${qualityColor}`}>
            {qualityScore}<span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">/10</span>
          </span>
        </div>
        <input
          type="range" min={1} max={10} value={qualityScore}
          onChange={e => setQualityScore(Number(e.target.value))}
          className="w-full accent-violet-500 cursor-pointer"
        />
        <div className="flex justify-between text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
          <span>Poor</span><span>Excellent</span>
        </div>
      </div>

      {/* Source */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Source</label>
        <select
          value={executionSource}
          onChange={e => setExecutionSource(e.target.value)}
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:border-[var(--slate)]"
        >
          <option value="manual">Manual entry</option>
          <option value="jira">Jira</option>
          <option value="linear">Linear</option>
          <option value="github">GitHub</option>
          <option value="webhook">Webhook</option>
          <option value="analytics">Analytics system</option>
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Notes <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(optional)</span></label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="What contributed to this outcome? What would you do differently?"
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)] resize-none"
        />
      </div>

      {error && (
        <p className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] dark:text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/50 rounded px-3 py-2">{error}</p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || !chosenItemId}
          className="flex-1 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 transition-colors"
        >
          {saving ? 'Saving…' : 'Record Outcome'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] border border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] dark:border-[var(--rule-bright)] transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
